import { useCallback, useEffect, useRef, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { OpeningFilterPopout } from '../ExecutionBoardFilters/ExecutionBoardFilters'
import { detectDateRangeMode, getDateRangeForMode } from './DateRangeFilterPopout.utils'

interface UseDateRangeFilterProps {
  setFilteringDateRange: (dateRange: { start: Date; end: Date } | null) => void
  currentDateRange?: { start?: Date; end?: Date }
  openingFilterPopout: OpeningFilterPopout | null
  onOpenFilterPopout: (popout: OpeningFilterPopout) => void
}

export const useDateRangeFilter = ({
  setFilteringDateRange,
  currentDateRange,
  openingFilterPopout,
  onOpenFilterPopout,
}: UseDateRangeFilterProps) => {
  const [selectedDateRange, setSelectedDateRange] = useState<{ start: Date; end: Date } | null>(
    null,
  )
  const [selectedDateRangeMode, setSelectedDateRangeMode] = useState<string | null>(null)

  const filterButtonRef = useRef<HTMLDivElement>(null)
  const dialogRef = useRef<HTMLDialogElement>(null)

  const [popoverPosition, setPopoverPosition] = useState(
    filterButtonRef.current?.getBoundingClientRect() ?? { bottom: 0, right: 0 },
  )

  const showPopover = (e: React.MouseEvent) => {
    e.stopPropagation()
    const pos = filterButtonRef.current?.getBoundingClientRect()
    if (pos) {
      setPopoverPosition(pos)
    }
    dialogRef.current?.showPopover()
    onOpenFilterPopout(OpeningFilterPopout.DATE_RANGE)
  }

  const handleClose = useCallback(
    (applyClose: boolean = false) => {
      if (dialogRef.current) {
        if (!applyClose) {
          const detected = detectDateRangeMode(currentDateRange)
          setSelectedDateRangeMode(detected.mode)
          setSelectedDateRange(detected.dateRange)
        }
        dialogRef.current.hidePopover()
      }
    },
    [currentDateRange],
  )

  const handleSelectDateRange = useCallback((dateRangeMode: string) => {
    setSelectedDateRangeMode(dateRangeMode)
    setSelectedDateRange(getDateRangeForMode(dateRangeMode))
  }, [])

  const handleClearAll = useCallback(() => {
    setSelectedDateRange(null)
    setFilteringDateRange(null)
    setSelectedDateRangeMode(null)
  }, [setFilteringDateRange])

  const handleApply = useCallback(() => {
    setFilteringDateRange(selectedDateRange)
    handleClose(true)
  }, [selectedDateRange, setFilteringDateRange, handleClose])

  const onChangeCustomDateRange = useCallback((dateRange: DateRange) => {
    if (dateRange && dateRange.from && dateRange.to) {
      setSelectedDateRange({
        start: dateRange.from,
        end: dateRange.to,
      })
    }
  }, [])

  useEffect(() => {
    if (openingFilterPopout !== OpeningFilterPopout.DATE_RANGE) {
      handleClose()
    }
  }, [openingFilterPopout, handleClose])

  return {
    selectedDateRange,
    selectedDateRangeMode,
    popoverPosition,
    filterButtonRef,
    dialogRef,
    showPopover,
    handleClose,
    handleSelectDateRange,
    handleClearAll,
    handleApply,
    onChangeCustomDateRange,
  }
}
